import { useState, useMemo, useRef, forwardRef } from "react"
import withStyles from "react-jss"
import { Howl } from "howler"
import Typewriter from "typewriter-effect/dist/core"
import audio1 from "../assets/audio/audio-1.wav"
import audio2 from "../assets/audio/audio-2.wav"
import audio3 from "../assets/audio/audio-3.wav"
import audio4 from "../assets/audio/audio-4.wav"
import audio5 from "../assets/audio/audio-5.wav"
import data from "../data"
import colors from "../styles/colors"
import { PlayButton, Sidebar } from "../components"

const widthVal = 30,
  width = `${widthVal}px`

const styles = {
  all: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "600px",
  },
  container: {
    position: "relative",
    display: "flex",
    cursor: "pointer",
  },
  right: {
    margin: `2rem 2rem 2rem calc(${width} + 1.5rem)`,
  },
  credits: {
    fontFamily: "BreveText",
    fontWeight: 300,
    fontSize: "10px",
    lineHeight: "14px",
    letterSpacing: "-0.25%",
    color: colors.creditsGrey,
    margin: `12px 0 12px ${widthVal / 2 - 2.5}px`,
    verticalAlign: "middle",
  },
  textWrapper: {
    position: "relative",
  },
  text: {
    fontFamily: "BreveText",
    fontWeight: 400,
    fontSize: "28px",
    lineHeight: "42px",
  },
  textHidden: {
    composes: "$text",
    opacity: 0,
  },
  textAnimated: {
    composes: "$text",
    position: "absolute",
    top: 0,
    left: 0,
  },
}

const PullQuote = ({ classes, id }, ref) => {
  const audioLabelMapping = {
    1: "testQuote",
    2: "edgeOfCreativity",
    3: "moveToMars",
    4: "soulOfAlien",
    5: "collabWithLLM",
  }
  const getAudioFile = (id) => {
    const mapping = {
      1: audio1,
      2: audio2,
      3: audio3,
      4: audio4,
      5: audio5,
    }
    return mapping[id]
  }
  const text = useMemo(() => data[id], [id])

  const [playing, setPlaying] = useState(false)

  const textRef = useRef(null)
  const typewriterRef = useRef(null)

  const audio = useRef(
    new Howl({
      src: [getAudioFile(id)],
      format: ["mp3"],
      onloaderror: () => {
        console.log("onload error")
      },
      onplayerror: () => {
        console.log("onplay error")
      },
      onend: () => {
        // Making sure that animation ends when audio ends
        // in case of slow animation
        setPlaying(false)
        let typewriter = typewriterRef.current
        if (typewriter) {
          typewriter.stop()
          textRef.current.textContent = text
        }
      },
    })
  )

  const handlePlayback = () => {
    if (!audio.current || !textRef.current) return
    const player = audio.current
    const isAudioPlaying = player.playing()
    const completedPlaying = player.seek() === 0 ? true : false

    let typewriter = typewriterRef.current
    /* If audio has already finished playing -
      1. Replay the audio
      2. start a new instance of animation
    */
    if (completedPlaying) {
      setPlaying(true)
      player.play()
      typewriter = new Typewriter(textRef.current, {
        delay: 50,
      })
      typewriter.deleteAll().typeString(text).start()
      typewriterRef.current = typewriter
    } else if (isAudioPlaying) {
      /* If audio is playing, pause both audio and animation */
      setPlaying(false)
      player.pause()
      typewriter.pause()
      if (window && window.parent && window.parent.dataLayer) {
        window.parent.dataLayer.push({
          event: `datavisualization-wiredGrimes-${audioLabelMapping[id]}-pause`,
        })
      }
    } else {
      /* If audio is paused in between, resume both */
      setPlaying(true)
      player.play()
      typewriter.start()
      if (window && window.parent && window.parent.dataLayer) {
        window.parent.dataLayer.push({
          event: `datavisualization-wiredGrimes-${audioLabelMapping[id]}-play`,
        })
      }
    }
  }

  return (
    <section className={classes.all} ref={ref}>
      <div className={classes.container} onClick={handlePlayback}>
        <Sidebar playing={playing} width={width} />
        <div className={classes.right}>
          <PlayButton playing={playing} />
          <div className={classes.textWrapper}>
            <p className={classes.textHidden}>{text}</p>
            <p className={classes.textAnimated} ref={textRef}>
              {text}
            </p>
          </div>
        </div>
      </div>
      <p className={classes.credits}>Audio generated by Elf.tech</p>
    </section>
  )
}

export default withStyles(styles)(forwardRef(PullQuote))
